import { useNavigateToCreatorStudio } from '@src/hooks/useNavigateToCreatorStudio';
import { saveFullVideoAsLightweightClip } from '@src/services/AIClipProducer';
import { downloadVideo } from '@src/services/videoDownloadService';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { Studio } from '@src/types.ts/general';
import { getLastActionTimestamp } from '@src/utility/lastAction';
import { getLastEditedClip } from '@src/utility/lastEditedClip';
import {
  getAllPublishInfo,
  getMostRecentPublishInfo,
} from '@src/utility/publishInfo';
import { useClickAway } from '@src/utility/useClickAway';
import { useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useOpenClipPreview } from '../useOpenClipPreview';

import type { OtherVideo } from '@src/types.ts/story';
import type { MutableRefObject } from 'react';

/**
 * Recency proxy for a clip: the most recent of its last publish and last save
 * (action). Used only as a fallback when there is no explicitly-tracked
 * last-edited clip; many clips carry no timestamps, so it is best-effort.
 */
const clipRecency = (video: OtherVideo): number => {
  const mostRecentPublish = getMostRecentPublishInfo(getAllPublishInfo(video));
  const publishTime = mostRecentPublish?.timestamp
    ? new Date(mostRecentPublish.timestamp).getTime()
    : 0;
  return Math.max(publishTime, getLastActionTimestamp(video));
};

interface EditVideoMenu {
  /** Ref for the dropdown root; closes the menu on outside click. */
  menuRef: MutableRefObject<HTMLDivElement | null>;
  menuOpen: boolean;
  toggleMenu: () => void;
  closeMenu: () => void;
  /** True while the "Content Studio" copy is being created. */
  isCreatingContentCopy: boolean;
  /** True while the original video is being downloaded. */
  isDownloadingOriginal: boolean;
  /** Main button: reopen the user's last-edited clip in Creator Studio. */
  onEditVideoClick: () => void;
  /** Dropdown: open a fresh copy of the original in Creator Studio. */
  onOpenCreatorStudio: () => void;
  /** Dropdown: open a fresh copy of the original in Content Studio. */
  onOpenInContentStudio: () => Promise<void>;
  /** Dropdown: download the original video as MP4 at the highest resolution. */
  onDownloadOriginal: () => Promise<void>;
}

/**
 * Logic for the Content Studio "Edit Video" split button + dropdown:
 * reopening the last-edited clip, and the two "open full video in …" options.
 */
export function useEditVideoMenu(): EditVideoMenu {
  const videoCreator = useVideoCreatorStore();
  const story = videoCreator?.story;
  const navigate = useNavigate();
  const [urlSearchParams] = useSearchParams();
  const navigateToCreatorStudio = useNavigateToCreatorStudio();
  const { open: openClipPreview } = useOpenClipPreview();

  const [menuOpen, setMenuOpen] = useState(false);
  const [isCreatingContentCopy, setIsCreatingContentCopy] = useState(false);
  const [isDownloadingOriginal, setIsDownloadingOriginal] = useState(false);
  const menuRef = useClickAway(() => setMenuOpen(false));

  const toggleMenu = useCallback(() => setMenuOpen(open => !open), []);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  const onOpenCreatorStudio = useCallback(() => {
    videoCreator.prepareCreatorStudioNavigation();
    // Mirror the Creator Studio "Duplicate Original" action: open a fresh
    // editable copy of the original rather than the published video. Drop any
    // inherited videoId so Creator Studio's init takes the "create from
    // original" branch instead of loading that specific clip.
    const params = new URLSearchParams(urlSearchParams);
    params.delete('videoId');
    params.set('newFromOriginal', 'true');
    navigate({
      pathname: `/${Studio.creator}`,
      search: params.toString(),
    });
  }, [videoCreator, navigate, urlSearchParams]);

  // "Content Studio" option: create a full-length editable copy of the story's
  // original video and open that copy in the clip-preview modal, leaving the
  // original untouched.
  const onOpenInContentStudio = useCallback(async () => {
    setMenuOpen(false);
    setIsCreatingContentCopy(true);
    try {
      const newClip = await saveFullVideoAsLightweightClip(
        videoCreator,
        story?.title ?? 'Untitled video',
      );
      if (!newClip) {
        videoCreator.showToast({
          state: 'error',
          message: 'Could not open the video in Content Studio.',
          delay: 3000,
        });
        return;
      }
      await openClipPreview(newClip, false);
    } finally {
      setIsCreatingContentCopy(false);
    }
  }, [videoCreator, story?.title, openClipPreview]);

  // "Download Original" option: download the story's original video as MP4 at
  // the highest resolution available. Unlike the player's preview URL (which
  // prefers ~720p for fast streaming), this prefers the original / 1080p so the
  // downloaded file is best quality; the || chain skips any rendition that
  // isn't ready yet and falls back to the raw source URL as a last resort.
  const onDownloadOriginal = useCallback(async () => {
    const original = story?.originalVideo;
    const video = original?.video;
    const url =
      video?.mp4Url ||
      video?.mp4UrlHd ||
      video?.mp4UrlHigh ||
      video?.mp4UrlMedium ||
      video?.mp4UrlLow ||
      original?.url;
    if (!url) {
      setMenuOpen(false);
      videoCreator.showToast({
        state: 'error',
        message: 'No downloadable video is available for this story.',
        delay: 3000,
      });
      return;
    }
    // Keep the menu open while the blob downloads so the in-item spinner shows
    // (mirrors the isCreatingContentCopy busy pattern); close it when done.
    setIsDownloadingOriginal(true);
    try {
      const result = await downloadVideo({
        url,
        metadata: {
          storyId: story?.id,
          storyTitle: story?.title,
          videoTitle: story?.title,
          sourcePlatform: 'content-studio',
        },
      });
      if (!result.ok) {
        videoCreator.showToast({
          state: 'error',
          message: 'Could not download the original video.',
          delay: 3000,
        });
      }
    } finally {
      setIsDownloadingOriginal(false);
      setMenuOpen(false);
    }
  }, [story?.originalVideo, story?.id, story?.title, videoCreator]);

  // Main button: reopen the clip the user most recently opened to edit.
  // Preference order: the explicitly-tracked last-edited clip (survives
  // Content ⇄ Creator navigation via localStorage), then the most-recently-
  // touched clip (max of last publish / last save) as a fallback when there is
  // no record yet. With no clips at all there is nothing to reopen, so the
  // button opens the dropdown instead.
  const onEditVideoClick = useCallback(() => {
    const clips = story?.otherVideos?.filter(v => !v.isHidden) ?? [];
    if (clips.length === 0) {
      setMenuOpen(open => !open);
      return;
    }

    const recordedId = getLastEditedClip(story?.id);
    const recordedMatch = recordedId
      ? clips.find(
          v =>
            v.id === recordedId ||
            v.associatedVideos?.some(av => av.id === recordedId),
        )
      : undefined;
    if (recordedMatch && recordedId) {
      // Open the exact variant the user last edited.
      navigateToCreatorStudio({ ...recordedMatch, id: recordedId });
      return;
    }

    const mostRecent = clips.reduce((latest, current) =>
      clipRecency(current) > clipRecency(latest) ? current : latest,
    );
    navigateToCreatorStudio(mostRecent);
  }, [story?.otherVideos, story?.id, navigateToCreatorStudio]);

  return {
    menuRef,
    menuOpen,
    toggleMenu,
    closeMenu,
    isCreatingContentCopy,
    isDownloadingOriginal,
    onEditVideoClick,
    onOpenCreatorStudio,
    onOpenInContentStudio,
    onDownloadOriginal,
  };
}
